/*
 * Copyright (c) CIRROM 2025.
 */

import {
  Card as MuiCard,
  CardContent as MuiCardContent,
  CardActionArea as MuiCardActionArea,
  cardActionAreaClasses,
} from "@mui/material";
import type { ElementType } from "react";
import styled from "@emotion/styled";
import { spacing } from "@mui/system";
import { MiraTheme } from "../../theme/theme";
import { css } from "@emotion/react";
import { rgba } from "polished";

export const MiraCard = styled(MuiCard)(spacing);

export const MiraCardContent = styled(MuiCardContent)<{ theme?: MiraTheme }>`
  &:last-child {
    padding-top: 0;
    padding-bottom: ${(props) => props.theme.spacing(4)};
  }
`;

const illustrationCardStyle = (props: any) => css`
  ${props.illustration &&
  props.theme.palette.mode !== "dark" &&
  `
    background: ${rgba(props.theme.palette.primary.main, 0.125)};
    color: ${props.theme.palette.primary.main};
  `}
`;

export const RelativeCard = styled(MuiCard)<{ illustration?: string }>`
  position: relative;

  ${illustrationCardStyle}
`;

export const RelativeClickableCard = styled(MuiCard)<{}>`
  cursor: pointer;
`;

export const RelativeAnimatedClickableCard = styled(MuiCard)<{
  theme?: MiraTheme;
}>`
  cursor: pointer;
  transition: all 0.3s ease;
  min-width: 0;
  overflow: hidden;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    border-color: ${(props) => props.theme.palette.primary.main};
  }
`;

/**
 * Card-sized link target, for a whole card that navigates.
 *
 * The card itself already answers the pointer with a lift and a primary-colour
 * border, so this contributes keyboard and focus behaviour only. MUI's default
 * focus highlight is suppressed: it fades a grey wash over the entire card on
 * hover, which sits on top of that lift and mutes the border colour change.
 * Keyboard focus is shown as a primary-colour outline instead, drawn inside
 * the card because the card clips its overflow.
 */
export const CardLinkArea = styled(MuiCardActionArea)<{
  theme?: MiraTheme;
  // Emotion's `styled` erases MUI's polymorphic `component` typing, so the
  // props needed to render the area as a router link are declared here.
  component?: ElementType;
  to?: string;
}>`
  &:hover
    .${cardActionAreaClasses.focusHighlight},
    &.${cardActionAreaClasses.focusVisible}
    .${cardActionAreaClasses.focusHighlight} {
    opacity: 0;
  }

  &.${cardActionAreaClasses.focusVisible} {
    outline: 2px solid ${(props) => props.theme.palette.primary.main};
    outline-offset: -2px;
  }
`;

export const RelativeCardContent = styled(MuiCardContent)<{
  theme?: MiraTheme;
}>`
  position: relative;
  min-width: 0;

  & .MuiTypography-root {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &:hover .MuiTypography-root {
    overflow: visible;
    text-overflow: clip;
    white-space: normal;
    overflow-wrap: anywhere;
  }

  &:last-child {
    padding-bottom: ${(props) => props.theme.spacing(4)};
  }
`;
